import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Camera, Save, Link, Calendar, Clock, Globe, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchedulerSettings() {
  const { user, token, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [profile, setProfile] = useState({
    display_name: '',
    bio: '',
    profile_photo_url: null,
    timezone: 'America/New_York',
    booking_buffer: 15,
    calendly_url: '',
    google_calendar_connected: false,
    is_accepting_bookings: true
  });

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ];

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduler/profile`, { headers: getAuthHeader() });
      setProfile({
        display_name: response.data.display_name || user?.name || '',
        bio: response.data.bio || '',
        profile_photo_url: response.data.profile_photo_url || null,
        timezone: response.data.timezone || 'America/New_York',
        booking_buffer: response.data.booking_buffer || 15,
        calendly_url: response.data.calendly_url || '',
        google_calendar_connected: response.data.google_calendar_connected || false,
        is_accepting_bookings: response.data.is_accepting_bookings !== false
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/scheduler/profile`, {
        display_name: profile.display_name,
        bio: profile.bio,
        timezone: profile.timezone,
        booking_buffer: profile.booking_buffer,
        calendly_url: profile.calendly_url || null,
        is_accepting_bookings: profile.is_accepting_bookings
      }, { headers: getAuthHeader() });
      toast.success('Profile saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/scheduler/profile/photo`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setProfile(prev => ({ ...prev, profile_photo_url: response.data.photo_url }));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGoogleConnect = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/scheduler/google-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleDisconnect = async () => {
    try {
      await axios.post(`${API_URL}/api/scheduler/google/disconnect`, {}, { headers: getAuthHeader() });
      setProfile(prev => ({ ...prev, google_calendar_connected: false }));
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/scheduler')} className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-foreground">Scheduler Settings</h1>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Profile Section */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-6">Profile Personalization</h2>
          
          {/* Photo Upload */}
          <div className="flex items-center gap-6 mb-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-muted overflow-hidden">
                {profile.profile_photo_url ? (
                  <img
                    src={profile.profile_photo_url.startsWith('http') ? profile.profile_photo_url : `${API_URL}${profile.profile_photo_url}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Camera size={32} />
                  </div>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 p-2 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera size={16} />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Profile Photo</h3>
              <p className="text-sm text-muted-foreground">This will be displayed on your booking page</p>
            </div>
          </div>

          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">Display Name *</label>
            <input
              type="text"
              value={profile.display_name}
              onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Your name as shown to clients"
              data-testid="display-name-input"
            />
          </div>

          {/* Bio */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">Bio</label>
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={4}
              placeholder="Tell potential clients about yourself, your expertise, and what you offer..."
              data-testid="bio-input"
            />
          </div>

          {/* Accepting Bookings Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <h3 className="font-medium text-foreground">Accepting Bookings</h3>
              <p className="text-sm text-muted-foreground">Turn off to hide your booking page temporarily</p>
            </div>
            <button
              onClick={() => setProfile({ ...profile, is_accepting_bookings: !profile.is_accepting_bookings })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                profile.is_accepting_bookings ? 'bg-green-500' : 'bg-muted'
              }`}
              data-testid="accepting-bookings-toggle"
            >
              <div className={`absolute top-1 w-4 h-4 bg-card rounded-full transition-transform ${
                profile.is_accepting_bookings ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Scheduling Settings */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-6">Scheduling Settings</h2>

          {/* Timezone */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">
              <Globe className="inline mr-1" size={16} />
              Timezone
            </label>
            <select
              value={profile.timezone}
              onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="timezone-select"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Buffer Time */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">
              <Clock className="inline mr-1" size={16} />
              Buffer Between Appointments
            </label>
            <select
              value={profile.booking_buffer}
              onChange={(e) => setProfile({ ...profile, booking_buffer: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="buffer-select"
            >
              <option value={0}>No buffer</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
            </select>
            <p className="text-sm text-muted-foreground mt-1">Time blocked off between consecutive appointments</p>
          </div>
        </div>

        {/* Calendar Integrations */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-6">Calendar Integrations</h2>

          {/* Google Calendar */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-card rounded-lg flex items-center justify-center shadow-sm">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Google Calendar</h3>
                <p className="text-sm text-muted-foreground">
                  {profile.google_calendar_connected ? 'Connected - bookings will sync automatically' : 'Sync your bookings automatically'}
                </p>
              </div>
            </div>
            {profile.google_calendar_connected ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={20} />
                <button
                  onClick={handleGoogleDisconnect}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={handleGoogleConnect}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                data-testid="connect-google-btn"
              >
                Connect
              </button>
            )}
          </div>

          {/* Calendly Integration */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-card rounded-lg flex items-center justify-center shadow-sm">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Calendly</h3>
                <p className="text-sm text-muted-foreground">Embed your existing Calendly on your booking page</p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={profile.calendly_url}
                onChange={(e) => setProfile({ ...profile, calendly_url: e.target.value })}
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                placeholder="https://calendly.com/your-username"
                data-testid="calendly-url-input"
              />
              {profile.calendly_url && (
                <a
                  href={profile.calendly_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink size={20} />
                </a>
              )}
            </div>
            {profile.calendly_url && (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <CheckCircle size={14} />
                Calendly will be embedded on your booking page
              </p>
            )}
          </div>

          {/* Apple Calendar Note */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h4 className="font-medium text-blue-900">Apple Calendar</h4>
              <p className="text-sm text-blue-700">
                Download .ics files from your bookings to add them to Apple Calendar, Outlook, or any other calendar app.
              </p>
            </div>
          </div>
        </div>

        {/* Preview Booking Page */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">Preview Your Booking Page</h3>
              <p className="text-indigo-100">See how clients will see your page</p>
            </div>
            <button
              onClick={() => window.open(`/book/${user?.id}`, '_blank')}
              className="flex items-center gap-2 px-4 py-2 bg-card/20 rounded-lg hover:bg-card/30 transition-colors"
              data-testid="preview-page-btn"
            >
              <ExternalLink size={18} />
              Open Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
